import type { OutputData } from '@editorjs/editorjs';

import type { EjsBlockData, EjsBlockType } from '../editor/editorjs/@types';
import { editorJsOutputDataToHtml } from '../editor/editorjs/editorjs-utils';
import type { MessageParsedEmbeds } from '../thread/ThreadMessage/threadTypes';
import type { ArrayGroupedBy } from '../types/utils';
import { html } from './dom';
import { parseJSONString } from './json';

export const getEjsEmbedsStringFiltered = (embeds: string, embedsTypesToKeep: EjsBlockType[]) => {
  const parsedEmbeds = parseJSONString<MessageParsedEmbeds>(embeds);
  if (!parsedEmbeds) return embeds;

  const filteredParsedEmbeds = getEjsParsedEmbedsFiltered(parsedEmbeds, embedsTypesToKeep);

  return JSON.stringify(filteredParsedEmbeds);
};

export const getEjsParsedEmbedsFiltered = (
  parsedEmbeds: MessageParsedEmbeds,
  embedsTypesToKeep: EjsBlockType[],
) => {
  const { blocks, ...rest } = parsedEmbeds;
  return {
    blocks: blocks.filter(b => embedsTypesToKeep.includes(b.type)),
    ...rest,
  };
};

const isOnlyOneBlock = (blocks: EjsBlockData[]) => blocks.length === 1;

export const getCardViewContent = (parsedEmbeds: MessageParsedEmbeds) => {
  const { blocks, ...rest } = parsedEmbeds;
  return isOnlyOneBlock(blocks)
    ? parsedEmbeds
    : {
        blocks: blocks.filter(b => b.type === 'paragraph'),
        ...rest,
      };
};

export const getSortedEjsEmbedsBlocks = <T extends EjsBlockData>(embedsBlocks: T[]) => {
  const sortedEmbeds: ArrayGroupedBy<T[], 'type'> = embedsBlocks?.reduce((acc, block) => {
    if (!acc[block.type]) acc[block.type] = [];
    acc[block.type].push(block);
    return acc;
  }, {});
  return sortedEmbeds;
};

export type EjsHighlightedEmbed = {
  type: EjsBlockType;
  blocks: EjsBlockData[];
};

export const getEjsHighlightedEmbed = (
  parsedEmbeds: MessageParsedEmbeds,
  limit?: number,
  priorityOrder: EjsBlockType[] = ['embed', 'image', 'googleForm', 'linkTool'],
) => {
  const { blocks } = parsedEmbeds;

  // First, let's group blocks by type
  const blocksByType = getSortedEjsEmbedsBlocks(blocks);

  // Iterate through priority order
  for (const type of priorityOrder) {
    const blocksOfType = blocksByType[type];

    if (blocksOfType && blocksOfType.length > 0) {
      // Special case for images: return all images
      if (type === 'image') {
        return {
          type,
          blocks: blocksOfType,
        };
      }

      // For other types, return limited number of blocks
      return {
        type,
        blocks: limit ? blocksOfType.slice(0, limit) : blocksOfType,
      };
    }
  }

  // If no blocks found in priority order, return null
  return null;
};

export const getComponentFromEjsOutputData = (embedBlocks: OutputData) =>
  html(editorJsOutputDataToHtml(embedBlocks));
